// repo/primary-care-connect/frontend/src/pages/Portal/CommunityNavigator/CaseDetail/intakeUnsavedState.ts

import type { IntakeFormState } from "./types";
import { buildIntakeAnswers } from "./intakeSummary";

/** Top-level Case Detail area where Intake leave protection applies. */
export const INTAKE_LEAVE_GUARD_AREA = "intake";

/**
 * Normalize Review-step save note the same way Save Progress / Complete Intake persist it:
 * trim; empty → null.
 */
export function normalizeIntakeSaveNote(
  value: string | null | undefined
): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed.length ? trimmed : null;
}

/**
 * Snapshot of everything Save Progress persists:
 * interview answers + Review-step save note.
 * Keeps buildIntakeAnswers() answers-only.
 */
export function buildIntakePersistSnapshot(form: IntakeFormState): string {
  return JSON.stringify({
    intakeAnswers: buildIntakeAnswers(form),
    note: normalizeIntakeSaveNote(form.note),
  });
}

/**
 * True when Intake has been hydrated and the editable persistable state
 * differs from the last successfully saved baseline.
 */
export function isIntakePersistDirty(
  form: IntakeFormState,
  savedBaselineSnapshot: string,
  intakeHydrated: boolean
): boolean {
  if (!intakeHydrated) return false;
  return buildIntakePersistSnapshot(form) !== savedBaselineSnapshot;
}

/**
 * Deep-clone IntakeFormState for restoreable saved-form snapshots.
 * Form values are plain JSON-serializable fields.
 */
export function cloneIntakeFormState(form: IntakeFormState): IntakeFormState {
  return JSON.parse(JSON.stringify(form)) as IntakeFormState;
}

/**
 * Intake leave warning is active only while the Intake work area is open
 * and the editable form differs from the last successfully persisted state.
 */
export function shouldGuardIntakeLeave(
  activeArea: string | null | undefined,
  isDirty: boolean
): boolean {
  return activeArea === INTAKE_LEAVE_GUARD_AREA && Boolean(isDirty);
}
